import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { GeoCoordinates } from '../utils/assetGeo';

export type UserLocationStatus = 'idle' | 'loading' | 'ready' | 'denied' | 'unsupported' | 'error';

type UserLocationContextValue = {
  status: UserLocationStatus;
  coordinates: GeoCoordinates | null;
  refresh: () => void;
};

const UserLocationContext = createContext<UserLocationContextValue>({
  status: 'idle',
  coordinates: null,
  refresh: () => {},
});

export function UserLocationProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<UserLocationStatus>('idle');
  const [coordinates, setCoordinates] = useState<GeoCoordinates | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unsupported');
      setCoordinates(null);
      return;
    }
    let cancelled = false;
    setStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) {
          return;
        }
        setCoordinates({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setStatus('ready');
      },
      (err) => {
        if (cancelled) {
          return;
        }
        setCoordinates(null);
        setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
      },
      { enableHighAccuracy: false, timeout: 12_000, maximumAge: 300_000 },
    );
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const value = useMemo(
    () => ({ status, coordinates, refresh }),
    [status, coordinates, refresh],
  );

  return <UserLocationContext.Provider value={value}>{children}</UserLocationContext.Provider>;
}

export function useUserLocationContext(): UserLocationContextValue {
  return useContext(UserLocationContext);
}
